import { Data, Effect } from 'effect';
import { ClientHttpError } from '../client';

type CommandSignal = 'SIGINT' | 'SIGTERM';
interface SignalHost {
  once(signal: CommandSignal, handler: () => void): unknown;
  removeListener(signal: CommandSignal, handler: () => void): unknown;
}

export class CommandSignalError extends Data.TaggedError('CommandSignalError')<{
  readonly signal: CommandSignal;
}> {}

/** Signal handlers belong to this invocation and are removed before it settles. */
export function withCommandSignalsEffect<A, E>(
  program: Effect.Effect<A, E>,
  host: SignalHost = process,
): Effect.Effect<A, E | CommandSignalError> {
  return Effect.suspend(() => {
    const handlers = new Map<CommandSignal, () => void>();
    const cancelled = Effect.callback<never, CommandSignalError>((resume) => {
      for (const signal of ['SIGINT', 'SIGTERM'] as const) {
        const handler = () => resume(Effect.fail(new CommandSignalError({ signal })));
        handlers.set(signal, handler);
        host.once(signal, handler);
      }
    }).pipe(
      Effect.ensuring(
        Effect.sync(() => {
          for (const [signal, handler] of handlers) host.removeListener(signal, handler);
        }),
      ),
    );
    return Effect.raceFirst(cancelled, program);
  });
}

/** Preserve normal signal exit codes, but join native cleanup before returning to Commander. */
export function runCommandEffect<A, E>(
  program: Effect.Effect<A, E>,
  host: SignalHost = process,
): Promise<A | undefined> {
  return Effect.runPromise(
    withCommandSignalsEffect(program, host).pipe(
      Effect.catch((error) => {
        if (error instanceof CommandSignalError) {
          return Effect.sync(() => {
            process.exitCode = error.signal === 'SIGINT' ? 130 : 143;
            return undefined;
          });
        }
        return Effect.fail(error instanceof ClientHttpError ? error.cause : error);
      }),
    ),
  );
}
